import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Link,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import "./ProductModal.css";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { HOST_URL } from "../../config/Url";
import { QRCodeSVG } from "qrcode.react";

const ProductModal = ({ product, handleClose }) => {
  const navigate = useNavigate();
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  if (!product) {
    return null;
  }

  const handlePdfOpen = (pdf) => {
    setSelectedPdf(pdf);
    setPdfDialogOpen(true);
  };

  const handlePdfClose = () => {
    setPdfDialogOpen(false);
    setSelectedPdf(null);
  };

  const handleMoreInfoClick = () => {
    navigate(`/product/${product.id}`);
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2000,
  };

  const imageFiles =
    product.fileResources?.filter((file) =>
      ["png", "jpg", "jpeg"].includes(file.fileType?.toLowerCase())
    ) || [];

  let pdfFiles =
    product.fileResources?.filter((file) =>
      ["pdf"].includes(file.fileType?.toLowerCase())
    ) || [];

  pdfFiles = pdfFiles.length > 0 ? [pdfFiles[pdfFiles.length - 1]] : [];

  const handleQuotation = () => {
    navigate("/contact-us");
  };

  // Function to render strength capsules from either string or array format
  const renderStrengthCapsules = () => {
    // Handle case when availableStrengths is a JSON string (from MariaDB)
    if (
      product.availableStrengths &&
      typeof product.availableStrengths === "string"
    ) {
      try {
        const parsedStrengths = JSON.parse(product.availableStrengths);
        if (Array.isArray(parsedStrengths) && parsedStrengths.length > 0) {
          return (
            <Box className="strength-capsules-container">
              {parsedStrengths.map((strength, index) => (
                <span key={index} className="strength-capsule">
                  {strength}
                </span>
              ))}
            </Box>
          );
        }
      } catch (e) {
        console.error("Error parsing availableStrengths:", e);
      }
    }

    // Check if availableStrengths is already an array
    if (
      product.availableStrengths &&
      Array.isArray(product.availableStrengths) &&
      product.availableStrengths.length > 0
    ) {
      return (
        <Box className="strength-capsules-container">
          {product.availableStrengths.map((strength, index) => (
            <span key={index} className="strength-capsule">
              {strength}
            </span>
          ))}
        </Box>
      );
    }
    // Fallback to the old string format if availableStrengths is not available
    else if (product.strength) {
      const strengths = product.strength
        .split(/[,;\/|]+/)
        .map((str) => str.trim())
        .filter(Boolean);

      if (strengths.length === 0) return "Not available";

      return (
        <Box className="strength-capsules-container">
          {strengths.map((strength, index) => (
            <span key={index} className="strength-capsule">
              {strength}
            </span>
          ))}
        </Box>
      );
    }

    return "Not available";
  };

  return (
    <>
      <Modal
        open={!!product}
        onClose={handleClose}
        aria-labelledby="product-modal-title"
        className="product-modal"
      >
        <Box
          className="modal-box"
          sx={{
            width: "90%",
            maxWidth: "800px",
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            position: "relative", // Added for proper positioning
          }}
        >
          <IconButton
            className="close-button"
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>

          <Grid container spacing={3}>
            {/* Left side - Image */}
            <Grid item xs={12} md={5}>
              {imageFiles.length > 0 ? (
                <Box sx={{ width: "100%", mb: 2 }}>
                  {imageFiles.length === 1 ? (
                    <img
                      src={`${HOST_URL}/${imageFiles[0].filePath}`}
                      alt={product.brandName}
                      style={{
                        width: "100%",
                        height: "300px",
                        objectFit: "contain",
                        backgroundColor: "#f0f0f0",
                      }}
                    />
                  ) : (
                    <Slider {...sliderSettings}>
                      {imageFiles.map((image, index) => (
                        <div key={index}>
                          <img
                            src={`${HOST_URL}/${image.filePath}`}
                            alt={`${product.brandName} - ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "300px",
                              objectFit: "contain",
                              backgroundColor: "#f0f0f0",
                            }}
                          />
                        </div>
                      ))}
                    </Slider>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "300px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  <Typography>No images available</Typography>
                </Box>
              )}
            </Grid>

            {/* Right side - Product Features Table */}
            <Grid item xs={12} md={7}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Product Features:
              </Typography>
              <Box sx={{ border: "1px solid #ccc", borderRadius: 1 }}>
                <Table
                  sx={{
                    border: "1px solid #ccc",
                    "& th, & td": {
                      border: "1px solid #ccc", // Add borders to all cells
                    },
                  }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{ fontWeight: "bold", width: "40%" }}
                      >
                        Brand Name
                      </TableCell>
                      <TableCell>{product.brandName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        Generic Name
                      </TableCell>
                      <TableCell>{product.genericName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        Manufacturer
                      </TableCell>
                      <TableCell>{product.manufacturer}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        Formulation
                      </TableCell>
                      <TableCell>{product.formulation}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        Available Strength
                      </TableCell>
                      <TableCell>{renderStrengthCapsules()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        Generic Brand
                      </TableCell>
                      <TableCell>{product.genericBrand}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Grid>
            <Grid item xs={12} md={12}>
              {/* Mail Us and WhatsApp Section */}
              <Box sx={{ mt: 3, border: "1px solid #ccc" }}>
                <Grid container>
                  {/* Mail Us Column */}
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                      borderRight: "1px solid #ccc",
                      "& .header": {
                        bgcolor: "#1f3864",
                        color: "white",
                        p: 1,
                        textAlign: "center",
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      className="header"
                      sx={{ padding: "8px 0 !important" }}
                    >
                      Mail Us
                    </Typography>
                    <Box sx={{ p: 2 }}>
                      <Link
                        href="mailto:info@sarpharma-international.com"
                        sx={{
                          display: "block",
                          color: "#0000EE",
                          textDecoration: "underline",
                          "&:hover": { color: "#0000EE" },
                        }}
                      >
                        info@sarpharma-international.com
                      </Link>
                      <Link
                        href="mailto:export.sarpharma@gmail.com"
                        sx={{
                          display: "block",
                          color: "#0000EE",
                          textDecoration: "underline",
                          "&:hover": { color: "#0000EE" },
                        }}
                      >
                        export.sarpharma@gmail.com
                      </Link>
                    </Box>
                  </Grid>

                  {/* WhatsApp Column */}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        bgcolor: "#1f3864",
                        color: "white",
                        p: 1,
                        textAlign: "center",
                      }}
                    >
                      Connect to WhatsApp for a quick response
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <QRCodeSVG
                        value="https://wa.me/+8801886212601"
                        size={120}
                        level="H"
                        includeMargin={true}
                        imageSettings={{
                          src: "/assets/images/whatsapp_logo.png",
                          x: undefined,
                          y: undefined,
                          height: 24,
                          width: 24,
                          excavate: true,
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleQuotation}
                sx={{ backgroundColor: "tomato", color: "white" }}
              >
                Ask for Quotation
              </Button>
            </Grid>
            <Grid item xs={12} md={12}>
              {/* Product Description */}
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ bgcolor: "#1f3864", color: "white", p: 1 }}
                >
                  Product Description
                </Typography>
                <Box
                  sx={{
                    mt: 2,
                    "& a": { color: "#11A193", textDecoration: "underline" },
                    "& ul, & ol": { paddingLeft: "20px" },
                    "& p": { marginBottom: "8px", textAlign: "justify" },
                    "& table": { borderCollapse: "collapse", width: "100%" },
                    "& th, & td": {
                      border: "1px solid #ddd",
                      padding: "8px",
                      textAlign: "left",
                    },
                    textAlign: "justify",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    maxWidth: "100%",
                    "& img": {
                      maxWidth: "100%",
                      height: "auto",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: product.description || "",
                  }}
                />

                {pdfFiles.map((pdf, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() =>
                        handlePdfOpen(`${HOST_URL}/${pdf.filePath}`)
                      }
                      color="primary"
                      fullWidth
                    >
                      See Details
                    </Button>
                  </Grid>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <Dialog
        open={pdfDialogOpen}
        onClose={handlePdfClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{product.brandName}</DialogTitle>
        <DialogContent>
          {selectedPdf && (
            <iframe
              src={`${selectedPdf}#toolbar=0`}
              width="100%"
              height="600px"
              title="PDF Viewer"
              frameBorder="0"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductModal;
